import {faDownload, faPrint} from '@fortawesome/free-solid-svg-icons'
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome'
import {GenericSelector} from '@freckle/educator-entities/ts/common/components/generic-selector'
import {HttpError} from '@freckle/educator-entities/ts/common/exceptions/http-error'
import type {SelectorItemT} from '@freckle/educator-entities/ts/common/logic/generic-selector'
import type {RlDomainIdT} from '@freckle/educator-entities/ts/common/types/rl-domain-id'
import {
  extractDataAndHeaders,
  fancyClient,
  downloadFile,
} from '@freckle/educator-entities/ts/openapi-clients'
import type {SchoolAttrs} from '@freckle/educator-entities/ts/roster/models/school'
import {Button} from '@freckle/educator-materials/ts/components/button'
import {ButtonAsync} from '@freckle/educator-materials/ts/components/button-async'
import {Dropdown} from '@freckle/educator-materials/ts/components/dropdown'
import {useSafeTranslation} from '@freckle/educator-materials/ts/helpers/translate'
import type {OptionType} from '@freckle/educator-materials/ts/logic/dropdown'
import {flatOptions as flatten} from '@freckle/educator-materials/ts/logic/dropdown'
import {logErrorAsUnhandled} from '@freckle/school/ts/common/helpers/exception-handlers/bugsnag-client'
import Notification from '@freckle/school/ts/common/helpers/notification'
import type {SummaryT} from '@freckle/school/ts/common/models/summary'
import type {IdentityProviderOrNoneT} from '@freckle/school/ts/manage-teachers/api-helpers/filters'
import {SourceDropdown} from '@freckle/school/ts/manage-teachers/components/source-dropdown'
import {
  filtersContainer,
  domainsFilter,
  gradeFilter,
  actionButtonsContainer,
  printButton,
  schoolDropdownStyle,
} from './proficiency-report-filters.module.scss'

type Props = {
  onSelectDomain: (domainId: RlDomainIdT) => void
  selectedDomainId: RlDomainIdT
  domainOptions: Array<SelectorItemT<RlDomainIdT>>
  onToggleSchoolSelection: (schools: SchoolAttrs[]) => void
  isSchoolSelected: (school: SchoolAttrs) => boolean
  selectedSchoolsIds: Array<number>
  paramsSchoolsIds: Array<number>
  schoolOptions: Set<SchoolAttrs>
  onSelectGrade: (grade: number) => void
  selectedGrade: number
  gradeOptions: Array<SelectorItemT<number>>
  selectedIdentityProviders: Array<IdentityProviderOrNoneT>
  onSelectIdentityProviders: (idps: Array<IdentityProviderOrNoneT>) => void
  summary: SummaryT
}

export default function ProficiencyReportFilters(props: Props) {
  const {t} = useSafeTranslation()
  const {selectedSchoolsIds, selectedDomainId, selectedGrade} = props

  const selectedSchools =
    props.paramsSchoolsIds.length === 0
      ? []
      : Array.from(props.schoolOptions).filter(school => props.isSchoolSelected(school))

  return (
    <div className={filtersContainer}>
      <GenericSelector
        onChangeSelection={props.onSelectGrade}
        selection={props.selectedGrade}
        collection={props.gradeOptions}
        className={gradeFilter}
      />
      <GenericSelector
        onChangeSelection={props.onSelectDomain}
        selection={props.selectedDomainId}
        collection={props.domainOptions}
        className={domainsFilter}
      />
      <Dropdown
        options={flatten(formatSchoolOptions(Array.from(props.schoolOptions)))}
        isSearchable={false}
        isMultiSelect
        handleChange={(schoolOptions: readonly OptionType<SchoolAttrs>[]) => {
          const schools = getSchoolSelectionOptions(schoolOptions)
          props.onToggleSchoolSelection(schools)
        }}
        value={formatSchoolOptions(selectedSchools)}
        dropdownClass={schoolDropdownStyle}
        noneSelectedText={t('NONE_SELECTED_TEXT')}
      />
      <SourceDropdown
        id="proficiency-report-filter-source"
        initSources={props.selectedIdentityProviders}
        summary={{tag: 'for-student', ...props.summary}}
        routeFn={props.onSelectIdentityProviders}
        labelText={''}
      />
      <div className={actionButtonsContainer}>
        <ButtonAsync
          className={`hidden-print ${printButton}`}
          style="secondary"
          onClick={() => downloadCsv(selectedSchoolsIds, selectedDomainId, selectedGrade)}
        >
          <FontAwesomeIcon icon={faDownload} />{' '}
          {t('PROFICIENCY_REPORT_BUTTON_DOWNLOAD_STUDENT_DATA')}
        </ButtonAsync>
        <Button className={`hidden-print ${printButton}`} style="secondary" onClick={window.print}>
          <FontAwesomeIcon icon={faPrint} /> {t('PROFICIENCY_REPORT_BUTTON_PRINT')}
        </Button>
      </div>
    </div>
  )
}

function formatSchoolOptions(schools: Array<SchoolAttrs>): Array<OptionType<SchoolAttrs>> {
  return schools.map(school => ({label: school.name, value: school}))
}

function getSchoolSelectionOptions(
  schoolOptions: readonly OptionType<SchoolAttrs>[],
): Array<SchoolAttrs> {
  return schoolOptions.map(({value}) => value)
}

function downloadCsv(
  schoolIds: Array<number>,
  domainId: RlDomainIdT,
  grade: number,
): Promise<void> {
  return fancyClient
    .GET(
      '/2/schools/{SchoolsMathAdaptiveGradeLevelReportR-NonEmptySchoolId}/reports/math-adaptive-grade-level',
      {
        headers: {
          Accept: 'text/csv',
        },
        parseAs: 'blob',
        params: {
          path: {
            'SchoolsMathAdaptiveGradeLevelReportR-NonEmptySchoolId': schoolIds,
          },
          query: {
            'domain-id': domainId,
            grade,
          },
        },
      },
    )
    .then(extractDataAndHeaders)
    .then(({data, headers}) => downloadFile(data, headers, 'report.csv'))
    .catch(error => {
      Notification.displayErrorNotification('Could not download the CSV at this time')
      logErrorAsUnhandled(new HttpError(error))
    })
}
